import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft,
  CalendarPlus,
  Check,
  FileText,
  Inbox as InboxIcon,
  Info,
  MessageSquare,
  Phone,
  Sparkles,
  Tag as TagIcon,
  TrendingUp,
  X,
  XCircle,
} from "lucide-react";

import { AllisonOrb } from "@/components/alia/allison-orb";
import { CommandPalette } from "@/components/alia/command-palette";
import { ConversationCard } from "@/components/alia/conversation-card";
import { EmptyState } from "@/components/alia/empty-state";
import { ErrorState } from "@/components/alia/error-state";
import { FilterPopover, FilterRow } from "@/components/alia/filter-popover";
import { HandoffToggleButton } from "@/components/alia/handoff-toggle-button";
import { LeadAvatar } from "@/components/alia/lead-avatar";
import { MessageInput } from "@/components/alia/message-input";
import { MetricCard } from "@/components/alia/metric-card";
import { PendingMessageBubble } from "@/components/alia/pending-message-bubble";
import { RefreshIndicator } from "@/components/alia/refresh-indicator";
import { ListSkeleton } from "@/components/alia/skeletons";
import { ChannelBadge, StatusPill } from "@/components/alia/status-pill";
import { TagPicker } from "@/components/alia/tag-picker";
import { Timeline } from "@/components/alia/timeline";
import { BottomSheet } from "@/components/layout/bottom-sheet";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { toast } from "@/components/ui/toast";
import { useAuth } from "@/hooks/use-auth";
import { useHotkeys } from "@/hooks/use-hotkeys";
import { useBreakpoint, useIsMobile } from "@/hooks/use-media-query";
import { usePolling } from "@/hooks/use-polling";
import { useSendMessage, type PendingMessage } from "@/hooks/use-send-message";
import { conversations as api } from "@/lib/api";
import {
  cn,
  formatCOP,
  relativeTime,
  STAGE_LABELS,
} from "@/lib/utils";
import type { Channel, Conversation, Message, Tag, TimelineEvent } from "@/types";
import type { InboxMetrics } from "@/data/mock-conversations";

type StatusFilter = "needs_attention" | "ai" | "human" | "resolved";

const STATUS_OPTIONS: { id: StatusFilter; label: string }[] = [
  { id: "needs_attention", label: "Necesitan atención" },
  { id: "ai", label: "Allison" },
  { id: "human", label: "Humano" },
  { id: "resolved", label: "Resueltos" },
];

const CHANNEL_OPTIONS: {
  id: Channel;
  label: string;
  dotClass: string;
}[] = [
  { id: "whatsapp", label: "WhatsApp", dotClass: "bg-channel-whatsapp" },
  { id: "web", label: "Web", dotClass: "bg-channel-web" },
  { id: "instagram", label: "Instagram", dotClass: "bg-channel-instagram" },
  { id: "telegram", label: "Telegram", dotClass: "bg-channel-telegram" },
];

export default function InboxPage() {
  const { conversationId } = useParams();
  const navigate = useNavigate();
  const { session } = useAuth();
  const isMobile = useIsMobile();
  const isXl = useBreakpoint("xl");

  const [selectedStatus, setSelectedStatus] = useState<Set<StatusFilter>>(new Set());
  const [selectedChannels, setSelectedChannels] = useState<Set<Channel>>(new Set());
  const [selectedTags, setSelectedTags] = useState<Set<string>>(new Set());
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [contextSheetOpen, setContextSheetOpen] = useState(false);
  const [pulsingLeadId, setPulsingLeadId] = useState<string | null>(null);

  const handleAutoTakeOver = useCallback((leadId: string) => {
    setPulsingLeadId(leadId);
    setTimeout(() => setPulsingLeadId((curr) => (curr === leadId ? null : curr)), 400);
  }, []);

  const fetchInbox = useCallback(async () => {
    const [list, metrics] = await Promise.all([
      api.list(session?.token),
      api.metrics(session?.token),
    ]);
    return { list, metrics };
  }, [session?.token]);

  const detectInboxChange = useCallback(
    (
      prev: { list: Conversation[]; metrics: InboxMetrics } | null,
      next: { list: Conversation[]; metrics: InboxMetrics }
    ) => {
      if (!prev) return false;
      if (prev.list.length !== next.list.length) return true;
      const prevTop = prev.list[0]?.updatedAt;
      const nextTop = next.list[0]?.updatedAt;
      return prevTop !== nextTop;
    },
    []
  );

  const {
    data: pollData,
    loading,
    refreshing,
    error: loadError,
    lastUpdatedAt,
    hasNewSinceLast,
    refresh,
  } = usePolling({
    fetcher: fetchInbox,
    intervalMs: 30_000,
    detectChange: detectInboxChange,
  });

  const items = pollData?.list ?? [];
  const metrics = pollData?.metrics ?? null;

  // Toast solo cuando el error es de refresh (ya hay datos cargados),
  // y solo cuando el mensaje cambia para no repetirlo en cada render.
  const lastErrorRef = useRef<string | null>(null);
  useEffect(() => {
    if (loadError && pollData !== null && loadError !== lastErrorRef.current) {
      toast.error(`No pudimos actualizar el inbox: ${loadError}`);
    }
    lastErrorRef.current = loadError;
  }, [loadError, pollData]);

  const [tagOverrides, setTagOverrides] = useState<Map<string, Tag[]>>(() => {
    try {
      const stored = sessionStorage.getItem("alia.tagOverrides");
      if (!stored) return new Map();
      const parsed = JSON.parse(stored) as Record<string, Tag[]>;
      return new Map(Object.entries(parsed));
    } catch {
      return new Map();
    }
  });

  useEffect(() => {
    try {
      const obj: Record<string, Tag[]> = {};
      tagOverrides.forEach((tags, id) => { obj[id] = tags; });
      sessionStorage.setItem("alia.tagOverrides", JSON.stringify(obj));
    } catch { /* quota exceeded, ignore */ }
  }, [tagOverrides]);

  const itemsWithLocalTags = useMemo<Conversation[]>(() => {
    if (tagOverrides.size === 0) return items;
    return items.map((c) =>
      tagOverrides.has(c.id) ? { ...c, tags: tagOverrides.get(c.id)! } : c
    );
  }, [items, tagOverrides]);

  const allLabels = useMemo(() => {
    const set = new Set<string>();
    for (const c of itemsWithLocalTags) for (const t of c.tags) set.add(t.label);
    return [...set].sort();
  }, [itemsWithLocalTags]);

  const tagFrequency = useMemo(() => {
    const map = new Map<string, number>();
    for (const c of itemsWithLocalTags) {
      for (const t of c.tags) {
        map.set(t.label, (map.get(t.label) ?? 0) + 1);
      }
    }
    return map;
  }, [itemsWithLocalTags]);

  function toggleTag(label: string) {
    setSelectedTags((prev) => {
      const next = new Set(prev);
      if (next.has(label)) next.delete(label);
      else next.add(label);
      return next;
    });
  }

  function addTagToConversation(conversationId: string, label: string) {
    setTagOverrides((prev) => {
      const next = new Map(prev);
      const conv = items.find((c) => c.id === conversationId);
      if (!conv) return prev;
      const currentTags = next.get(conversationId) ?? conv.tags;
      if (currentTags.some((t) => t.label.toLowerCase() === label.toLowerCase())) {
        return prev;
      }
      const newTag: Tag = {
        id: `t_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
        label,
        source: "human",
        createdAt: new Date().toISOString(),
      };
      next.set(conversationId, [...currentTags, newTag]);
      return next;
    });
  }

  function removeTagFromConversation(conversationId: string, tagId: string) {
    setTagOverrides((prev) => {
      const next = new Map(prev);
      const conv = items.find((c) => c.id === conversationId);
      if (!conv) return prev;
      const currentTags = next.get(conversationId) ?? conv.tags;
      next.set(conversationId, currentTags.filter((t) => t.id !== tagId));
      return next;
    });
  }

  const filtered = useMemo(() => {
    let list = itemsWithLocalTags;

    if (selectedStatus.size > 0) {
      list = list.filter((c) => {
        for (const s of selectedStatus) {
          if (s === "needs_attention") {
            if (c.status === "waiting" || c.unread > 0) return true;
          } else if (c.status === s) {
            return true;
          }
        }
        return false;
      });
    }

    if (selectedChannels.size > 0) {
      list = list.filter((c) => selectedChannels.has(c.channel));
    }

    if (selectedTags.size > 0) {
      list = list.filter((c) => {
        const labels = new Set(c.tags.map((t) => t.label));
        for (const required of selectedTags) {
          if (!labels.has(required)) return false;
        }
        return true;
      });
    }

    return list;
  }, [itemsWithLocalTags, selectedStatus, selectedChannels, selectedTags]);

  const totalFiltersActive =
    selectedStatus.size + selectedChannels.size + selectedTags.size;

  function clearAllFilters() {
    setSelectedStatus(new Set());
    setSelectedChannels(new Set());
    setSelectedTags(new Set());
  }

  function toggleStatus(s: StatusFilter) {
    setSelectedStatus((prev) => {
      const next = new Set(prev);
      if (next.has(s)) next.delete(s);
      else next.add(s);
      return next;
    });
  }

  function toggleChannel(c: Channel) {
    setSelectedChannels((prev) => {
      const next = new Set(prev);
      if (next.has(c)) next.delete(c);
      else next.add(c);
      return next;
    });
  }

  function countStatus(s: StatusFilter): number {
    return items.filter((c) => {
      if (s === "needs_attention") return c.status === "waiting" || c.unread > 0;
      return c.status === s;
    }).length;
  }

  function countChannel(c: Channel): number {
    return items.filter((conv) => conv.channel === c).length;
  }

  const active = useMemo(
    () => itemsWithLocalTags.find((c) => c.id === conversationId) ?? null,
    [itemsWithLocalTags, conversationId]
  );

  const activeIdx = active ? filtered.findIndex((c) => c.id === active.id) : -1;

  useHotkeys(
    {
      "mod+k": () => setPaletteOpen(true),
      "/": () => setPaletteOpen(true),
      j: () => {
        if (isMobile) return;
        if (filtered.length === 0) return;
        const next = activeIdx < 0 ? 0 : Math.min(activeIdx + 1, filtered.length - 1);
        navigate(`/inbox/${filtered[next].id}`);
      },
      k: () => {
        if (isMobile) return;
        if (filtered.length === 0) return;
        const next = activeIdx <= 0 ? 0 : activeIdx - 1;
        navigate(`/inbox/${filtered[next].id}`);
      },
      Escape: () => {
        if (paletteOpen) setPaletteOpen(false);
        else if (contextSheetOpen) setContextSheetOpen(false);
        else if (active) navigate("/inbox");
      },
    },
    [filtered, activeIdx, paletteOpen, active, navigate, isMobile, contextSheetOpen]
  );

  const listView = (
    <ListPane
      loading={loading}
      loadError={loadError}
      filtered={filtered}
      lastUpdatedAt={lastUpdatedAt}
      refreshing={refreshing}
      hasNewSinceLast={hasNewSinceLast}
      refresh={refresh}
      onSearchClick={() => setPaletteOpen(true)}
      metrics={metrics}
      selectedStatus={selectedStatus}
      selectedChannels={selectedChannels}
      selectedTags={selectedTags}
      allLabels={allLabels}
      tagFrequency={tagFrequency}
      countStatus={countStatus}
      countChannel={countChannel}
      toggleStatus={toggleStatus}
      toggleChannel={toggleChannel}
      toggleTag={toggleTag}
      setSelectedStatus={setSelectedStatus}
      setSelectedChannels={setSelectedChannels}
      setSelectedTags={setSelectedTags}
      totalFiltersActive={totalFiltersActive}
      clearAllFilters={clearAllFilters}
      isMobile={isMobile}
    />
  );

  // ============== MOBILE ==============
  if (isMobile) {
    return (
      <>
        <CommandPalette
          open={paletteOpen}
          onClose={() => setPaletteOpen(false)}
          conversations={items}
        />

        <div className="flex h-full flex-col bg-background">
          {active ? (
            <MobileConversationView
              conversation={active}
              onBack={() => navigate("/inbox")}
              onOpenContext={() => setContextSheetOpen(true)}
              token={session?.token}
              refresh={refresh}
              onAutoTakeOver={handleAutoTakeOver}
              pulsingLeadId={pulsingLeadId}
            />
          ) : (
            listView
          )}
        </div>

        {active && (
          <BottomSheet
            open={contextSheetOpen}
            onClose={() => setContextSheetOpen(false)}
            title="Detalle del lead"
            height="full"
          >
            <ConversationContext
              conversation={active}
              availableLabels={allLabels}
              onAddTag={(label) => addTagToConversation(active.id, label)}
              onRemoveTag={(tagId) =>
                removeTagFromConversation(active.id, tagId)
              }
            />
          </BottomSheet>
        )}
      </>
    );
  }

  // ============== DESKTOP ==============
  return (
    <>
      <CommandPalette
        open={paletteOpen}
        onClose={() => setPaletteOpen(false)}
        conversations={items}
      />

      <div
        className={cn(
          "grid h-full",
          isXl
            ? "grid-cols-[420px_1fr_340px]"
            : "grid-cols-[420px_1fr]"
        )}
      >
        {listView}

        <section className="flex h-full flex-col overflow-hidden bg-surface">
          {active ? (
            <ConversationDetail
              conversation={active}
              onOpenContext={!isXl ? () => setContextSheetOpen(true) : undefined}
              token={session?.token}
              refresh={refresh}
              onAutoTakeOver={handleAutoTakeOver}
              pulsingLeadId={pulsingLeadId}
            />
          ) : (
            <EmptyStateDetail />
          )}
        </section>

        {isXl && (
          <aside className="hidden h-full flex-col overflow-y-auto border-l border-border bg-background xl:flex">
            {active ? (
              <ConversationContext
                conversation={active}
                availableLabels={allLabels}
                onAddTag={(label) => addTagToConversation(active.id, label)}
                onRemoveTag={(tagId) =>
                  removeTagFromConversation(active.id, tagId)
                }
              />
            ) : (
              <div className="p-6">
                <p className="text-xs text-muted-foreground">
                  Detalles del lead aparecerán aquí.
                </p>
              </div>
            )}
          </aside>
        )}
      </div>

      {!isXl && active && (
        <BottomSheet
          open={contextSheetOpen}
          onClose={() => setContextSheetOpen(false)}
          title="Detalle del lead"
          height="full"
        >
          <ConversationContext
            conversation={active}
            availableLabels={allLabels}
            onAddTag={(label) => addTagToConversation(active.id, label)}
            onRemoveTag={(tagId) =>
              removeTagFromConversation(active.id, tagId)
            }
          />
        </BottomSheet>
      )}
    </>
  );
}

/* ========================================================================== */
/*  LIST PANE                                                                 */
/* ========================================================================== */

interface ListPaneProps {
  loading: boolean;
  loadError: string | null;
  filtered: Conversation[];
  lastUpdatedAt: Date | null;
  refreshing: boolean;
  hasNewSinceLast: boolean;
  refresh: () => Promise<void>;
  onSearchClick: () => void;
  metrics: InboxMetrics | null;
  selectedStatus: Set<StatusFilter>;
  selectedChannels: Set<Channel>;
  selectedTags: Set<string>;
  allLabels: string[];
  tagFrequency: Map<string, number>;
  countStatus: (s: StatusFilter) => number;
  countChannel: (c: Channel) => number;
  toggleStatus: (s: StatusFilter) => void;
  toggleChannel: (c: Channel) => void;
  toggleTag: (label: string) => void;
  setSelectedStatus: (s: Set<StatusFilter>) => void;
  setSelectedChannels: (c: Set<Channel>) => void;
  setSelectedTags: (t: Set<string>) => void;
  totalFiltersActive: number;
  clearAllFilters: () => void;
  isMobile: boolean;
}

function ListPane({
  loading,
  loadError,
  filtered,
  lastUpdatedAt,
  refreshing,
  hasNewSinceLast,
  refresh,
  onSearchClick,
  metrics,
  selectedStatus,
  selectedChannels,
  selectedTags,
  allLabels,
  tagFrequency,
  countStatus,
  countChannel,
  toggleStatus,
  toggleChannel,
  toggleTag,
  setSelectedStatus,
  setSelectedChannels,
  setSelectedTags,
  totalFiltersActive,
  clearAllFilters,
  isMobile,
}: ListPaneProps) {
  return (
    <section
      className={cn(
        "flex h-full flex-col overflow-hidden bg-background",
        !isMobile && "border-r border-border"
      )}
    >
      <div className={cn("space-y-3 lg:space-y-4", isMobile ? "px-4 pt-4" : "px-5 pt-6")}>
        <div>
          <div className="flex items-center justify-between gap-2">
            <h1
              className={cn(
                "font-semibold tracking-tight text-foreground",
                isMobile ? "text-lg" : "text-xl"
              )}
            >
              Inbox
            </h1>
            <div className="flex items-center gap-1.5">
              <RefreshIndicator
                lastUpdatedAt={lastUpdatedAt}
                refreshing={refreshing}
                hasNewSinceLast={hasNewSinceLast}
                onRefresh={refresh}
                errorMessage={loadError}
              />
              {!isMobile && (
                <button
                  onClick={onSearchClick}
                  className="inline-flex items-center gap-1.5 rounded-md border border-border bg-surface-2 px-2 py-1 text-[11px] text-muted-foreground transition-colors hover:border-border-strong hover:text-foreground"
                >
                  <span>Buscar</span>
                  <kbd className="font-mono text-[10px]">⌘K</kbd>
                </button>
              )}
            </div>
          </div>
          <p
            className={cn(
              "mt-0.5 text-muted-foreground",
              isMobile ? "text-xs" : "text-[13px]"
            )}
          >
            Conversaciones de todos los canales en un mismo lugar.
          </p>
        </div>

        <div className="grid grid-cols-2 gap-2">
          <MetricCard
            label="Necesitan atención"
            value={metrics?.needsAttention ?? "—"}
            icon={InboxIcon}
            tone="warning"
            trend={metrics?.trendNeedsAttention}
            delta={metrics?.deltaNeedsAttention}
            goodWhenUp={false}
          />
          <MetricCard
            label="Allison atendió hoy"
            value={metrics?.aiHandled ?? "—"}
            icon={Sparkles}
            tone="brand"
            trend={metrics?.trendAiHandled}
            delta={metrics?.deltaAiHandled}
          />
        </div>

        <div className="flex flex-wrap items-center gap-1.5">
          <FilterPopover
            label="Estado"
            activeCount={selectedStatus.size}
            onClear={() => setSelectedStatus(new Set())}
            width={220}
          >
            {STATUS_OPTIONS.map((opt) => (
              <FilterRow
                key={opt.id}
                selected={selectedStatus.has(opt.id)}
                onClick={() => toggleStatus(opt.id)}
                count={countStatus(opt.id)}
              >
                {opt.label}
              </FilterRow>
            ))}
          </FilterPopover>

          <FilterPopover
            label="Canal"
            activeCount={selectedChannels.size}
            onClear={() => setSelectedChannels(new Set())}
            width={200}
          >
            {CHANNEL_OPTIONS.map((opt) => (
              <FilterRow
                key={opt.id}
                selected={selectedChannels.has(opt.id)}
                onClick={() => toggleChannel(opt.id)}
                count={countChannel(opt.id)}
                prefix={
                  <span
                    className={cn(
                      "size-1.5 shrink-0 rounded-full",
                      opt.dotClass
                    )}
                  />
                }
              >
                {opt.label}
              </FilterRow>
            ))}
          </FilterPopover>

          {allLabels.length > 0 && (
            <FilterPopover
              label="Tags"
              activeCount={selectedTags.size}
              onClear={() => setSelectedTags(new Set())}
              width={240}
            >
              {allLabels.map((label) => (
                <FilterRow
                  key={label}
                  selected={selectedTags.has(label)}
                  onClick={() => toggleTag(label)}
                  count={tagFrequency.get(label) ?? 0}
                >
                  <span className="lowercase">{label}</span>
                </FilterRow>
              ))}
            </FilterPopover>
          )}

          {totalFiltersActive > 0 && (
            <button
              onClick={clearAllFilters}
              className="ml-auto inline-flex items-center gap-1 rounded-md px-2 py-1 text-[11px] text-muted-foreground hover:text-foreground"
            >
              <X className="size-3" />
              Limpiar todo
            </button>
          )}

          {isMobile && totalFiltersActive === 0 && (
            <button
              onClick={onSearchClick}
              className="ml-auto inline-flex items-center gap-1.5 rounded-md border border-border bg-surface-2 px-2.5 py-1.5 text-xs text-muted-foreground"
            >
              Buscar
            </button>
          )}
        </div>
      </div>

      <Separator className={cn(isMobile ? "mt-3" : "mt-4")} />

      <div className={cn("flex-1 overflow-y-auto py-3", isMobile ? "px-2" : "px-3")}>
        {loading ? (
          <ListSkeleton />
        ) : loadError && filtered.length === 0 ? (
          <ErrorState
            message={loadError}
            onRetry={() => refresh()}
          />
        ) : filtered.length === 0 ? (
          <EmptyState
            icon={InboxIcon}
            title="Sin conversaciones"
            description="Cuando entren nuevos mensajes aparecerán aquí."
          />
        ) : (
          <ul className="space-y-2">
            {filtered.map((c) => (
              <li key={c.id} className="animate-fade-in">
                <ConversationCard conversation={c} />
              </li>
            ))}
          </ul>
        )}
      </div>

      {!isMobile && (
        <div className="flex items-center justify-center gap-3 border-t border-border bg-surface px-4 py-2 text-[10px] text-muted-foreground">
          <Hint k="J/K">Navegar</Hint>
          <Hint k="↵">Abrir</Hint>
          <Hint k="⌘K">Buscar</Hint>
        </div>
      )}
    </section>
  );
}

/* ========================================================================== */
/*  MOBILE CONVERSATION VIEW                                                  */
/* ========================================================================== */

function MobileConversationView({
  conversation,
  onBack,
  onOpenContext,
  token,
  refresh,
  onAutoTakeOver,
  pulsingLeadId,
}: {
  conversation: Conversation;
  onBack: () => void;
  onOpenContext: () => void;
  token: string | undefined;
  refresh: () => Promise<void>;
  onAutoTakeOver: (leadId: string) => void;
  pulsingLeadId: string | null;
}) {
  const isPulsing = pulsingLeadId === conversation.lead.id;
  return (
    <div className="flex h-full flex-col bg-surface">
      <header className="sticky top-0 z-10 flex items-center gap-3 border-b border-border bg-surface/95 px-3 py-2.5 backdrop-blur">
        <button
          onClick={onBack}
          className="flex size-10 shrink-0 items-center justify-center rounded-lg text-foreground transition-colors hover:bg-surface-2 active:bg-surface-2"
          aria-label="Volver a lista"
        >
          <ArrowLeft className="size-5" />
        </button>

        <div className="flex min-w-0 flex-1 items-center gap-2.5">
          <motion.div
            animate={isPulsing ? { scale: [1, 1.08, 1] } : { scale: 1 }}
            transition={{ duration: 0.4 }}
          >
            <LeadAvatar name={conversation.lead.name} size={36} />
          </motion.div>
          <div className="min-w-0 flex-1">
            <h2 className="truncate text-sm font-semibold text-foreground">
              {conversation.lead.name}
            </h2>
            <div className="mt-0.5 flex items-center gap-1.5">
              <ChannelBadge channel={conversation.channel} />
              <StatusPill status={conversation.status} />
            </div>
          </div>
        </div>

        <HandoffToggleButton
          conversation={conversation}
          token={token}
          refresh={refresh}
          iconOnly
        />

        <button
          onClick={onOpenContext}
          className="flex size-10 shrink-0 items-center justify-center rounded-lg text-foreground transition-colors hover:bg-surface-2 active:bg-surface-2"
          aria-label="Ver detalle del lead"
        >
          <Info className="size-5" />
        </button>
      </header>

      <ConversationDetail
        conversation={conversation}
        mobileMode
        token={token}
        refresh={refresh}
        onAutoTakeOver={onAutoTakeOver}
        pulsingLeadId={pulsingLeadId}
      />
    </div>
  );
}

/* ========================================================================== */
/*  Skeleton + EmptyStateDetail                                               */
/* ========================================================================== */

function EmptyStateDetail() {
  return (
    <div className="flex h-full flex-col items-center justify-center px-8 py-16 text-center">
      <div className="relative mb-6 flex size-20 items-center justify-center">
        <span className="absolute inset-0 animate-pulse-slow rounded-full bg-brand/10" />
        <MessageSquare className="size-7 text-muted-foreground" />
      </div>
      <h3 className="text-base font-semibold text-foreground">
        Selecciona una conversación
      </h3>
      <p className="mt-2 max-w-sm text-sm leading-relaxed text-muted-foreground">
        Elige una de la lista o presiona{" "}
        <kbd className="mx-0.5 inline-flex h-5 items-center rounded border border-border bg-surface-2 px-1.5 font-mono text-[10px]">
          ⌘K
        </kbd>{" "}
        para buscar.
      </p>
    </div>
  );
}

/* ========================================================================== */
/*  ConversationDetail                                                        */
/* ========================================================================== */

function ConversationDetail({
  conversation,
  mobileMode = false,
  onOpenContext,
  token,
  refresh,
  onAutoTakeOver,
  pulsingLeadId,
}: {
  conversation: Conversation;
  mobileMode?: boolean;
  onOpenContext?: () => void;
  token: string | undefined;
  refresh: () => Promise<void>;
  onAutoTakeOver: (leadId: string) => void;
  pulsingLeadId: string | null;
}) {
  const handleAutoTakeOver = useCallback(() => {
    onAutoTakeOver(conversation.lead.id);
  }, [onAutoTakeOver, conversation.lead.id]);

  const { pending, send, retry, dismiss } = useSendMessage({
    leadId: conversation.lead.id,
    token,
    conversation,
    onSent: refresh,
    onAutoTakeOver: handleAutoTakeOver,
  });

  // Bloquear input solo cuando hay un failed sin resolver (retry/dismiss).
  // Múltiples 'sending' en paralelo están permitidos — cada bubble settle
  // independiente cuando vuelve su response (estándar messaging apps).
  const hasPendingFailure = pending.some((p) => p.status === "failed");
  const isPulsing = pulsingLeadId === conversation.lead.id;

  type FeedItem =
    | { kind: "message"; data: Message; sortAt: number }
    | { kind: "event"; data: TimelineEvent; sortAt: number }
    | { kind: "pending"; data: PendingMessage; sortAt: number };

  const feed = useMemo<FeedItem[]>(() => {
    const items: FeedItem[] = [
      ...conversation.messages.map(
        (m) =>
          ({
            kind: "message",
            data: m,
            sortAt: new Date(m.createdAt).getTime(),
          }) as const
      ),
      ...conversation.events.map(
        (e) =>
          ({
            kind: "event",
            data: e,
            sortAt: new Date(e.createdAt).getTime(),
          }) as const
      ),
      ...pending.map(
        (p) =>
          ({
            kind: "pending",
            data: p,
            sortAt: new Date(p.createdAt).getTime(),
          }) as const
      ),
    ];
    items.sort((a, b) => a.sortAt - b.sortAt);
    return items;
  }, [conversation, pending]);

  return (
    <>
      {!mobileMode && (
        <header className="flex items-center justify-between gap-4 border-b border-border px-4 py-4 lg:px-6">
          <div className="flex min-w-0 items-center gap-3">
            <motion.div
              animate={isPulsing ? { scale: [1, 1.08, 1] } : { scale: 1 }}
              transition={{ duration: 0.4 }}
            >
              <LeadAvatar name={conversation.lead.name} size={40} />
            </motion.div>
            <div className="min-w-0">
              <h2 className="truncate text-[15px] font-semibold text-foreground">
                {conversation.lead.name}
              </h2>
              <div className="mt-0.5 flex items-center gap-1.5">
                <ChannelBadge channel={conversation.channel} />
                <StatusPill status={conversation.status} />
              </div>
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <Button variant="ghost" size="sm">
              <Phone className="size-4" />
              <span className="hidden sm:inline">Llamar</span>
            </Button>
            <HandoffToggleButton
              conversation={conversation}
              token={token}
              refresh={refresh}
            />
            {onOpenContext && (
              <button
                onClick={onOpenContext}
                className="flex size-9 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-surface-2 hover:text-foreground"
                aria-label="Ver detalle del lead"
              >
                <Info className="size-4" />
              </button>
            )}
          </div>
        </header>
      )}

      <div className={cn("flex-1 overflow-y-auto", mobileMode ? "px-3 py-4" : "px-4 py-6 lg:px-6 lg:py-8")}>
        <div className={cn("mx-auto space-y-4", mobileMode ? "max-w-full" : "max-w-2xl")}>
          {feed.map((item) => {
            if (item.kind === "message") {
              return <MessageBubble key={item.data.id} message={item.data} mobileMode={mobileMode} />;
            }
            if (item.kind === "event") {
              return <InlineEvent key={item.data.id} event={item.data} />;
            }
            return (
              <PendingMessageBubble
                key={item.data.tempId}
                pending={item.data}
                mobileMode={mobileMode}
                onRetry={retry}
                onDismiss={dismiss}
              />
            );
          })}
        </div>
      </div>

      <div className={cn("border-t border-border bg-surface", mobileMode ? "px-3 py-3" : "px-4 py-4 lg:px-6")}>
        <MessageInput
          channel={conversation.channel}
          hasPendingFailure={hasPendingFailure}
          onSend={send}
          mobileMode={mobileMode}
        />
      </div>
    </>
  );
}

/* ========================================================================== */
/*  MessageBubble                                                             */
/* ========================================================================== */

function MessageBubble({ message, mobileMode = false }: { message: Message; mobileMode?: boolean }) {
  const isOutgoing = message.role === "agent";
  const isAi = message.source === "allison";

  return (
    <div
      className={cn(
        "flex gap-3 animate-fade-in",
        isOutgoing ? "flex-row-reverse" : "flex-row"
      )}
    >
      <div className="shrink-0">
        {isAi ? (
          <div className="flex size-8 items-center justify-center rounded-full border border-brand/30 bg-brand/10">
            <AllisonOrb size={18} />
          </div>
        ) : (
          <div className="flex size-8 items-center justify-center rounded-full bg-surface-3 text-[10px] font-semibold text-muted-foreground">
            {message.role === "agent" ? "TÚ" : "—"}
          </div>
        )}
      </div>

      <div
        className={cn(
          "rounded-2xl px-4 py-2.5 leading-relaxed text-sm",
          mobileMode ? "max-w-[85%]" : "max-w-[75%]",
          isOutgoing
            ? isAi
              ? "border border-brand/30 bg-brand/[0.08] text-foreground"
              : "bg-surface-3 text-foreground"
            : "border border-border bg-surface-2 text-foreground"
        )}
      >
        {isAi && (
          <div className="mb-1 flex items-center gap-1.5">
            <span className="text-[11px] font-semibold text-brand">
              Allison
            </span>
            <span className="text-[11px] text-muted-foreground">
              · respuesta automática
            </span>
          </div>
        )}
        <p>{message.text}</p>
        <time className="mt-1 block text-[11px] text-muted-foreground">
          {relativeTime(message.createdAt)}
        </time>
      </div>
    </div>
  );
}

/* ========================================================================== */
/*  InlineEvent                                                               */
/* ========================================================================== */

function InlineEvent({ event }: { event: TimelineEvent }) {
  return (
    <div className="flex items-center gap-3 py-1 animate-fade-in">
      <div className="h-px flex-1 bg-border" />
      <div className="flex items-center gap-2 rounded-full border border-border bg-surface-2 px-3 py-1 text-[11px] text-muted-foreground">
        <Sparkles className="size-3 text-brand" />
        <span className="font-medium text-foreground">{event.label}</span>
        {event.detail && (
          <>
            <span className="text-border">·</span>
            <span>{event.detail}</span>
          </>
        )}
        <span className="text-border">·</span>
        <time className="tabular-nums">{relativeTime(event.createdAt)}</time>
      </div>
      <div className="h-px flex-1 bg-border" />
    </div>
  );
}

/* ========================================================================== */
/*  ConversationContext                                                       */
/* ========================================================================== */

function ConversationContext({
  conversation,
  availableLabels,
  onAddTag,
  onRemoveTag,
}: {
  conversation: Conversation;
  availableLabels: string[];
  onAddTag: (label: string) => void;
  onRemoveTag: (tagId: string) => void;
}) {
  const { lead } = conversation;

  return (
    <div className="space-y-6 p-4 lg:p-5">
      <div className="rounded-xl border border-border bg-surface p-4">
        <div className="flex items-center gap-3">
          <LeadAvatar name={lead.name} size={48} />
          <div className="min-w-0 flex-1">
            <h3 className="truncate text-[15px] font-semibold text-foreground">
              {lead.name}
            </h3>
            <p className="text-xs text-muted-foreground">
              {lead.city ?? "Sin ciudad"} · {STAGE_LABELS[lead.stage]}
            </p>
          </div>
        </div>

        {lead.debt && (
          <div className="mt-4 rounded-lg border border-border bg-surface-2 p-3">
            <div className="flex items-center justify-between">
              <span className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                Deuda total
              </span>
              <TrendingUp className="size-3.5 text-brand" />
            </div>
            <p className="mt-1.5 text-xl font-semibold tabular-nums text-foreground">
              {formatCOP(lead.debt)}
            </p>
          </div>
        )}

        <dl className="mt-4 space-y-2 text-sm">
          <Row label="Teléfono" value={lead.phone ?? "—"} />
          <Row label="Email" value={lead.email ?? "—"} />
          {conversation.aiResponseSeconds && (
            <Row label="Allison responde en">
              <span className="font-medium tabular-nums text-foreground">
                {conversation.aiResponseSeconds}s
              </span>
            </Row>
          )}
        </dl>
      </div>

      <div>
        <h3 className="mb-2 flex items-center gap-2 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
          <TagIcon className="size-3" />
          Tags
        </h3>
        <TagPicker
          selected={conversation.tags}
          availableLabels={availableLabels}
          onAdd={onAddTag}
          onRemove={onRemoveTag}
        />
        {conversation.tags.some((t) => t.source === "marcus") && (
          <p className="mt-2 flex items-center gap-1 text-[10px] text-muted-foreground">
            <Sparkles className="size-2.5 text-brand" />
            Tags con ✨ las clasifica Marcus
          </p>
        )}
      </div>

      <div>
        <h3 className="mb-3 flex items-center gap-2 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
          <Sparkles className="size-3" />
          Timeline del caso
        </h3>
        <Timeline events={conversation.events} />
      </div>

      <Separator />

      <div>
        <h3 className="mb-3 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
          Acciones rápidas
        </h3>
        <div className="space-y-2">
          <ActionButton
            icon={FileText}
            label="Generar propuesta económica"
            description="Plan de pagos basado en deuda actual"
          />
          <ActionButton
            icon={CalendarPlus}
            label="Agendar cita con Margie"
            description="Calendario · 30 minutos"
          />
          <ActionButton
            icon={Check}
            label="Marcar como ganado"
            tone="success"
          />
          <ActionButton
            icon={XCircle}
            label="Marcar como descartado"
            tone="muted"
          />
        </div>
      </div>
    </div>
  );
}

function ActionButton({
  icon: Icon,
  label,
  description,
  tone = "default",
}: {
  icon: typeof FileText;
  label: string;
  description?: string;
  tone?: "default" | "success" | "muted";
}) {
  const toneClasses = {
    default: "hover:border-brand/30 hover:bg-brand/[0.04]",
    success: "hover:border-success/30 hover:bg-success/[0.04]",
    muted: "hover:border-border-strong",
  };
  const iconTone = {
    default: "text-brand",
    success: "text-success",
    muted: "text-muted-foreground",
  };

  return (
    <button
      className={cn(
        "group flex w-full items-start gap-3 rounded-lg border border-border bg-surface p-3 text-left transition-all min-h-12",
        toneClasses[tone]
      )}
    >
      <div className="mt-0.5 flex size-7 shrink-0 items-center justify-center rounded-md bg-surface-2">
        <Icon className={cn("size-3.5", iconTone[tone])} />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-[13px] font-medium text-foreground">{label}</p>
        {description && (
          <p className="mt-0.5 text-[11px] text-muted-foreground">
            {description}
          </p>
        )}
      </div>
    </button>
  );
}

/* ========================================================================== */
/*  Helpers                                                                   */
/* ========================================================================== */

function Row({
  label,
  value,
  children,
}: {
  label: string;
  value?: string;
  children?: ReactNode;
}) {
  return (
    <div className="flex items-center justify-between gap-3 text-xs">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="font-medium text-foreground">{children ?? value}</dd>
    </div>
  );
}

function Hint({ k, children }: { k: string; children: ReactNode }) {
  return (
    <span className="inline-flex items-center gap-1">
      <kbd className="inline-flex h-4 min-w-4 items-center justify-center rounded border border-border bg-surface-2 px-1 font-mono text-[9px] font-medium text-muted-foreground">
        {k}
      </kbd>
      <span>{children}</span>
    </span>
  );
}

void Badge;
